import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Spinner,
  Table,
  Row,
  Col,
} from "reactstrap";
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  PieChart, Pie, Cell, ResponsiveContainer 
} from "recharts";
import { getSurveyAnalytics, getSurveyResponses } from "../../../helpers/surveyApi";
import { dangerMessage, successMessage } from "../../Toast/Toast";
import { FaFileDownload, FaChartBar, FaUsers, FaCheckCircle, FaListUl } from "react-icons/fa";

const SurveyAnalytics = ({ surveyId, token, onClose }) => {
  const [analytics, setAnalytics] = useState(null);
  const [responses, setResponses] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const COLORS = ['#f97316', '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899'];

  const fetchAnalytics = useCallback(async () => {
    setIsLoading(true);
    try {
      const [analyticsResult, responsesResult] = await Promise.all([
        getSurveyAnalytics(surveyId, token),
        getSurveyResponses(surveyId, token),
      ]);

      if (analyticsResult?.status === "success") {
        setAnalytics(analyticsResult.data);
      }
      if (responsesResult?.status === "success") {
        setResponses(responsesResult.data);
      }
    } catch (error) {
      dangerMessage("Error loading analytics");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [surveyId, token]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const handleExportCSV = () => {
    if (!responses || !responses.length) {
      dangerMessage("No responses to export");
      return;
    }

    try {
      // ✅ Get questions from questionAnalytics (which IS returned by backend)
      // Fallback to analytics.questions if available
      const questions = analytics?.questions || 
        analytics?.questionAnalytics?.map(qa => ({
          question: qa.question,
          type: qa.type,
        })) || [];

      // ✅ Create headers
      const headers = [
        "Response ID",
        "Submitted At",
        "Email",
        ...questions.map((q, idx) => `Q${idx + 1}: ${q.question}`)
      ];

      // ✅ Map responses to CSV rows
      const rows = responses.map((response) => {
        const row = [
          response._id || "",
          response.submittedAt ? new Date(response.submittedAt).toLocaleString() : "",
          response.attendeeEmail || "Anonymous",
        ];
        
        // ✅ Add answers for each question
        questions.forEach((question, questionIndex) => {
          const questionId = questionIndex.toString();
          const responseItem = response.responses?.find(
            (r) => r.questionId === questionId
          );
          
          // ✅ Handle different answer types
          let answer = "";
          if (responseItem) {
            if (typeof responseItem.answer === 'object') {
              answer = Array.isArray(responseItem.answer) 
                ? responseItem.answer.join(', ') 
                : JSON.stringify(responseItem.answer);
            } else {
              answer = String(responseItem.answer);
            }
          }
          
          row.push(answer);
        });
        
        return row;
      });

      // ✅ Create CSV content with proper escaping
      const csvContent = [
        headers.join(","),
        ...rows.map(row => 
          row.map(cell => {
            const escaped = String(cell).replace(/"/g, '""');
            return `"${escaped}"`;
          }).join(",")
        )
      ].join("\n");

      // ✅ Create and download file
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `survey-responses-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      successMessage("CSV exported successfully");
    } catch (error) {
      console.error("CSV Export Error:", error);
      dangerMessage("Failed to export CSV");
    }
  };

  const renderQuestionAnalytics = (question, index) => {
    if (!analytics?.questionAnalytics?.[index]) {
      return null;
    }

    const questionData = analytics.questionAnalytics[index];
    const questionType = question.type;

    // Shared card styles
    const cardStyle = {
      border: "none",
      borderRadius: "12px",
      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
      marginBottom: "24px",
      overflow: "hidden",
      background: "white",
    };

    const cardHeaderStyle = {
      background: "white",
      borderBottom: "2px solid #f3f4f6",
      padding: "20px 24px",
      fontWeight: "600",
      fontSize: "1.1rem",
      color: "#1f2937",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: "10px",
    };

    const cardBodyStyle = {
      padding: "24px",
    };

    if (questionType === "rating") {
      const ratingData = questionData.ratingDistribution || {};
      const chartData = Object.keys(ratingData)
        .map((key) => ({
          rating: key,
          count: ratingData[key],
        }))
        .sort((a, b) => parseInt(a.rating) - parseInt(b.rating));

      return (
        <Card key={index} style={cardStyle}>
          <div style={cardHeaderStyle}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <FaChartBar style={{ color: "#f97316" }} />
              <span>{question.question}</span>
            </div>
            <span style={{ fontSize: "13px", color: "#6b7280", fontWeight: "500" }}>
              Avg: {questionData.averageRating?.toFixed(2) || "N/A"} ⭐
            </span>
          </div>
          <div style={cardBodyStyle}>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="rating" 
                  tick={{ fontSize: 12, fill: "#6b7280" }}
                  axisLine={{ stroke: "#e5e7eb" }}
                />
                <YAxis 
                  tick={{ fontSize: 12, fill: "#6b7280" }}
                  axisLine={{ stroke: "#e5e7eb" }}
                />
                <Tooltip 
                  contentStyle={{ 
                    borderRadius: "8px", 
                    border: "none", 
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    fontSize: "12px"
                  }}
                />
                <Bar 
                  dataKey="count" 
                  fill="#f97316" 
                  radius={[6, 6, 0, 0]}
                  animationDuration={500}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      );
    }

    if (questionType === "multipleChoice" || questionType === "yesNo") {
      const sourceData = questionType === "multipleChoice" 
        ? questionData.choiceDistribution || {} 
        : questionData.yesNoDistribution || {};
      
      const chartData = Object.keys(sourceData).map((key) => ({
        name: key,
        value: sourceData[key],
      }));

      const total = chartData.reduce((sum, item) => sum + item.value, 0);

      return (
        <Card key={index} style={cardStyle}>
          <div style={cardHeaderStyle}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <FaChartBar style={{ color: "#f97316" }} />
              <span>{question.question}</span>
            </div>
            <span style={{ fontSize: "13px", color: "#6b7280", fontWeight: "500" }}>
              {total} responses
            </span>
          </div>
          <div style={cardBodyStyle}>
            <Row className="align-items-center">
              <Col xs="12" md="6" className="mb-3 mb-md-0">
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={3}
                      dataKey="value"
                      animationDuration={500}
                    >
                      {chartData.map((entry, idx) => (
                        <Cell 
                          key={`cell-${idx}`} 
                          fill={COLORS[idx % COLORS.length]} 
                          stroke="white"
                          strokeWidth={2}
                        />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        borderRadius: "8px", 
                        border: "none", 
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                        fontSize: "12px"
                      }}
                      formatter={(value) => [`${value} (${((value/total)*100).toFixed(1)}%)`, "Responses"]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </Col>
              <Col xs="12" md="6">
                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  {chartData.map((entry, idx) => (
                    <div 
                      key={idx} 
                      style={{ 
                        display: "flex", 
                        alignItems: "center", 
                        gap: "10px",
                        padding: "8px 12px",
                        background: "#f9fafb",
                        borderRadius: "8px",
                        border: "1px solid #e5e7eb"
                      }}
                    >
                      <div 
                        style={{ 
                          width: "14px", 
                          height: "14px", 
                          borderRadius: "4px",
                          background: COLORS[idx % COLORS.length] 
                        }} 
                      />
                      <span style={{ fontSize: "13px", fontWeight: "500", color: "#374151", flex: 1 }}>
                        {entry.name}
                      </span>
                      <span style={{ fontSize: "13px", color: "#6b7280", fontWeight: "600" }}>
                        {entry.value} ({((entry.value/total)*100).toFixed(1)}%)
                      </span>
                    </div>
                  ))}
                </div>
              </Col>
            </Row>
          </div>
        </Card>
      );
    }

    if (questionType === "text") {
      const textResponses = responses
        ?.filter((r) => r.responses?.some((resp) => resp.questionId === index.toString()))
        .map((r) => ({
          email: r.attendeeEmail || "Anonymous",
          answer: r.responses?.find((resp) => resp.questionId === index.toString())?.answer || "",
          submittedAt: r.submittedAt || "",
        }))
        .slice(0, 10) || [];

      return (
        <Card key={index} style={cardStyle}>
          <div style={cardHeaderStyle}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <FaListUl style={{ color: "#f97316" }} />
              <span>{question.question}</span>
            </div>
            <span style={{ fontSize: "13px", color: "#6b7280", fontWeight: "500" }}>
              {textResponses.length} of {questionData.totalResponses || 0} shown
            </span>
          </div>
          <div style={cardBodyStyle}>
            {textResponses.length === 0 ? (
              <div style={{ textAlign: "center", padding: "40px 20px", color: "#6b7280" }}>
                No text responses yet
              </div>
            ) : (
              <div style={{ 
                maxHeight: "400px", 
                overflowY: "auto", 
                border: "1px solid #e5e7eb", 
                borderRadius: "10px" 
              }}>
                <Table hover responsive className="mb-0">
                  <thead style={{ position: "sticky", top: 0, background: "#f9fafb", zIndex: 1 }}>
                    <tr>
                      <th style={{ fontWeight: "600", fontSize: "13px", color: "#6b7280", padding: "12px 16px" }}>Respondent</th>
                      <th style={{ fontWeight: "600", fontSize: "13px", color: "#6b7280", padding: "12px 16px" }}>Response</th>
                      <th style={{ fontWeight: "600", fontSize: "13px", color: "#6b7280", padding: "12px 16px" }}>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {textResponses.map((response, idx) => (
                      <tr key={idx}>
                        <td style={{ padding: "12px 16px", fontSize: "13px", color: "#374151", fontWeight: "500" }}>
                          {response.email}
                        </td>
                        <td style={{ padding: "12px 16px", fontSize: "13px", color: "#374151", maxWidth: "300px" }}>
                          <span style={{ 
                            display: "-webkit-box", 
                            WebkitLineClamp: 2, 
                            WebkitBoxOrient: "vertical", 
                            overflow: "hidden",
                            lineHeight: "1.4"
                          }}>
                            {response.answer}
                          </span>
                        </td>
                        <td style={{ padding: "12px 16px", fontSize: "12px", color: "#6b7280", whiteSpace: "nowrap" }}>
                          {response.submittedAt ? new Date(response.submittedAt).toLocaleDateString() : "N/A"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </div>
        </Card>
      );
    }

    return null;
  };

  const totalQuestions = useMemo(() => {
    if (!analytics) return 0;
    return (
      analytics.totalQuestions ??
      (Array.isArray(analytics.questions) ? analytics.questions.length : undefined) ??
      (Array.isArray(analytics.questionAnalytics) ? analytics.questionAnalytics.length : undefined) ?? 0
    );
  }, [analytics]);

  const formattedResponses = useMemo(() => {
    if (!responses || !responses.length) return [];
    return responses.map((r, idx) => {
      const email = r.attendeeEmail;
      const isAnonymous = !email;
      const initials = email ? email.slice(0, 2).toUpperCase() : "AN";
      return {
        key: r._id || idx,
        index: idx + 1,
        email,
        isAnonymous,
        initials,
        domain: email ? email.split("@")[1] || "Email" : "No email captured",
        submittedLabel: r.submittedAt ? new Date(r.submittedAt).toLocaleString() : "N/A",
      };
    });
  }, [responses]);

  // Shared styles matching SurveyForm
  const styles = {
    container: {
      maxWidth: "1200px",
      margin: "0 auto",
      padding: "20px",
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
      backgroundColor: "#f8f9fa",
      minHeight: "100vh",
    },
    headerSection: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      marginBottom: "30px",
      flexWrap: "wrap",
      gap: "15px",
    },
    card: {
      border: "none",
      borderRadius: "12px",
      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
      marginBottom: "24px",
      overflow: "hidden",
      background: "white",
    },
    statCard: {
      background: "white",
      borderRadius: "12px",
      padding: "20px",
      border: "1px solid #e5e7eb",
      height: "100%",
      transition: "all 0.2s",
    },
    statValue: {
      fontSize: "28px",
      fontWeight: "700",
      margin: "8px 0",
    },
    statLabel: {
      fontSize: "12px",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
      fontWeight: "600",
      color: "#6b7280",
    },
    btnCustom: {
      borderRadius: "8px",
      padding: "10px 20px",
      fontWeight: "500",
      transition: "all 0.2s",
      cursor: "pointer",
      border: "none",
      fontSize: "14px",
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
    },
    btnPrimary: {
      background: "#f97316",
      color: "white",
    },
    btnSecondary: {
      background: "white",
      color: "#374151",
      border: "1px solid #d1d5db",
    },
    emptyState: {
      textAlign: "center",
      padding: "60px 20px",
      background: "#f9fafb",
      borderRadius: "12px",
      border: "1px dashed #e5e7eb",
    },
    respondentsTable: {
      maxHeight: "320px",
      overflowY: "auto",
      borderRadius: "10px",
      border: "1px solid #e5e7eb",
    },
  };

  if (isLoading) {
    return (
      <div style={styles.container}>
        <div style={{ 
          display: "flex", 
          justifyContent: "center", 
          alignItems: "center", 
          minHeight: "60vh" 
        }}>
          <div style={{ textAlign: "center" }}>
            <Spinner color="primary" />
            <div style={{ marginTop: "16px", color: "#6b7280", fontSize: "14px" }}>
              Loading survey analytics...
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Custom CSS for responsive enhancements */}
      <style>{`
        @media (max-width: 768px) {
          .stat-card-mobile {
            margin-bottom: 12px !important;
          }
          .respondents-table-mobile th,
          .respondents-table-mobile td {
            padding: 10px 12px !important;
            font-size: 12px !important;
          }
          .chart-container-mobile {
            height: 220px !important;
          }
        }
        @media (max-width: 480px) {
          .chart-container-mobile {
            height: 200px !important;
          }
        }
      `}</style>

      {/* Header */}
      <div style={styles.headerSection}>
        <div>
          <h3 style={{ margin: 0, color: "#1f2937", fontWeight: "700", fontSize: "1.5rem" }}>
            Survey Analytics
          </h3>
          <p style={{ margin: "5px 0 0", color: "#6b7280", fontSize: "14px" }}>
            Overview of responses and engagement for this survey
          </p>
        </div>
        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
          <button 
            style={{ ...styles.btnCustom, ...styles.btnPrimary }}
            onClick={handleExportCSV}
          >
            <FaFileDownload />
            Export CSV
          </button>
          <button 
            style={{ ...styles.btnCustom, ...styles.btnSecondary }}
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>

      {analytics && (
        <>
          {/* Summary Stats */}
          <Card style={styles.card}>
            <CardHeader style={{ 
              background: "white",
              borderBottom: "2px solid #f3f4f6",
              padding: "20px 24px",
              fontWeight: "600",
              fontSize: "1.1rem",
              color: "#1f2937",
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}>
              <FaChartBar style={{ color: "#f97316" }} />
              <span>Summary</span>
            </CardHeader>
            <CardBody style={{ padding: "24px" }}>
              <Row className="g-3">
                <Col xs="12" md="4">
                  <div style={{ ...styles.statCard, backgroundColor: "#eff6ff", borderColor: "#bfdbfe" }} className="stat-card-mobile">
                    <div style={styles.statLabel}>Total Responses</div>
                    <div style={{ ...styles.statValue, color: "#2563eb" }}>
                      {analytics.totalResponses || 0}
                    </div>
                    <div style={{ fontSize: "12px", color: "#6b7280" }}>
                      <FaUsers style={{ marginRight: "4px" }} />
                      Submissions received
                    </div>
                  </div>
                </Col>
                <Col xs="12" md="4">
                  <div style={{ ...styles.statCard, backgroundColor: "#ecfdf3", borderColor: "#bbf7d0" }} className="stat-card-mobile">
                    <div style={styles.statLabel}>Completion Rate</div>
                    <div style={{ ...styles.statValue, color: "#16a34a" }}>
                      {(() => {
                        if (analytics.completionRate == null) return "N/A";
                        const raw = Number(analytics.completionRate) || 0;
                        const normalised = raw > 1 ? raw : raw * 100;
                        const clamped = Math.max(0, Math.min(normalised, 100));
                        return `${clamped.toFixed(1)}%`;
                      })()}
                    </div>
                    <div style={{ fontSize: "12px", color: "#6b7280" }}>
                      <FaCheckCircle style={{ marginRight: "4px" }} />
                      Questions answered
                    </div>
                  </div>
                </Col>
                <Col xs="12" md="4">
                  <div style={{ ...styles.statCard, backgroundColor: "#fefce8", borderColor: "#fde68a" }} className="stat-card-mobile">
                    <div style={styles.statLabel}>Total Questions</div>
                    <div style={{ ...styles.statValue, color: "#a16207" }}>
                      {totalQuestions}
                    </div>
                    <div style={{ fontSize: "12px", color: "#6b7280" }}>
                      <FaListUl style={{ marginRight: "4px" }} />
                      In this survey
                    </div>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>

          {/* Respondent List */}
          {formattedResponses.length > 0 && (
            <Card style={styles.card}>
              <CardHeader style={{ 
                background: "white",
                borderBottom: "2px solid #f3f4f6",
                padding: "20px 24px",
                fontWeight: "600",
                fontSize: "1.1rem",
                color: "#1f2937",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "10px",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <FaUsers style={{ color: "#f97316" }} />
                  <span>Respondents</span>
                </div>
                <span style={{ 
                  background: "#f3f4f6", 
                  color: "#6b7280", 
                  padding: "4px 12px", 
                  borderRadius: "20px", 
                  fontSize: "12px",
                  fontWeight: "500"
                }}>
                  {formattedResponses.length} {formattedResponses.length === 1 ? "response" : "responses"}
                </span>
              </CardHeader>
              <CardBody style={{ padding: "0 24px 24px" }}>
                <div style={styles.respondentsTable}>
                  <Table hover responsive className="mb-0 respondents-table-mobile">
                    <thead style={{ 
                      position: "sticky", 
                      top: 0, 
                      background: "#f9fafb", 
                      zIndex: 1,
                      borderBottom: "1px solid #e5e7eb"
                    }}>
                      <tr>
                        <th style={{ fontWeight: "600", fontSize: "13px", color: "#6b7280", padding: "12px 16px", width: "60px" }}>#</th>
                        <th style={{ fontWeight: "600", fontSize: "13px", color: "#6b7280", padding: "12px 16px" }}>Respondent</th>
                        <th style={{ fontWeight: "600", fontSize: "13px", color: "#6b7280", padding: "12px 16px" }}>Submitted</th>
                      </tr>
                    </thead>
                    <tbody>
                      {formattedResponses.map((r) => (
                        <tr key={r.key}>
                          <td style={{ padding: "12px 16px", fontSize: "13px", color: "#6b7280", fontWeight: "500" }}>
                            {r.index}
                          </td>
                          <td style={{ padding: "12px 16px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                              <div style={{
                                width: "36px",
                                height: "36px",
                                borderRadius: "50%",
                                backgroundColor: r.isAnonymous ? "#e5e7eb" : "#dbeafe",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "13px",
                                fontWeight: "600",
                                color: "#1f2937",
                                flexShrink: 0,
                              }}>
                                {r.initials}
                              </div>
                              <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                                <span style={{ fontWeight: "500", fontSize: "14px", color: "#374151" }}>
                                  {r.email || "Anonymous respondent"}
                                </span>
                                <span style={{ fontSize: "12px", color: "#9ca3af" }}>
                                  {r.domain}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td style={{ padding: "12px 16px", fontSize: "13px", color: "#6b7280", whiteSpace: "nowrap" }}>
                            {r.submittedLabel}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </CardBody>
            </Card>
          )}

          {/* Question Analytics */}
          {analytics.questions?.map((question, index) =>
            renderQuestionAnalytics(question, index)
          )}
        </>
      )}

      {/* Empty State */}
      {(!analytics || analytics.totalResponses === 0) && (
        <Card style={styles.card}>
          <CardBody style={{ padding: "60px 24px" }}>
            <div style={styles.emptyState}>
              <div style={{ 
                width: "64px", 
                height: "64px", 
                background: "#e5e7eb", 
                borderRadius: "50%", 
                margin: "0 auto 20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#9ca3af"
              }}>
                <FaChartBar size={24} />
              </div>
              <h5 style={{ margin: "0 0 12px", color: "#374151", fontWeight: "600" }}>
                No responses yet
              </h5>
              <p style={{ margin: 0, color: "#6b7280", fontSize: "14px", maxWidth: "400px", marginInline: "auto" }}>
                Share your survey to start collecting feedback and view analytics here.
              </p>
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
};

export default React.memo(SurveyAnalytics);